import React, { useState } from 'react';
import {
  BookOpen,
  Rocket,
  Search,
  ArrowRight,
  Database,
  Layers,
  FileText,
  Settings,
  Zap
} from 'lucide-react';
import { DocumentationViewer } from '../components/DocumentationViewer';

interface DocCategory {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  color: string;
  description: string;
  docs: DocItem[];
}

interface DocItem {
  title: string;
  filePath: string; // Path relative to /wiki/docs_prod/
  description: string;
  location: string;
}

export const DocumentationHub: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [viewingDoc, setViewingDoc] = useState<DocItem | null>(null);
  const [markdown, setMarkdown] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Production documents from docs_prod folder - only files that actually exist
  const categories: DocCategory[] = [
    {
      id: 'getting-started',
      name: 'Getting Started',
      icon: Rocket,
      color: 'primary',
      description: 'Setup guides, installation, and quick start documentation',
      docs: [
        {
          title: 'Port Allocation',
          filePath: 'PORT_ALLOCATION.md',
          description: 'Port configuration for all Peak Priorities services',
          location: 'docs_prod/PORT_ALLOCATION.md'
        },
        {
          title: 'Development Guide',
          filePath: 'DEVELOPMENT.md',
          description: 'Development workflow and best practices',
          location: 'docs_prod/DEVELOPMENT.md'
        },
        {
          title: 'Deployment Guide',
          filePath: 'DEPLOYMENT.md',
          description: 'Complete deployment guide',
          location: 'docs_prod/DEPLOYMENT.md'
        },
        {
          title: 'README',
          filePath: 'README.md',
          description: 'Production documentation overview',
          location: 'docs_prod/README.md'
        }
      ]
    },
    {
      id: 'architecture',
      name: 'Architecture & Technical',
      icon: Layers,
      color: 'sage',
      description: 'System architecture, technical specifications, and implementation details',
      docs: [
        {
          title: 'Technical Architecture',
          filePath: 'TECHNICAL_ARCHITECTURE.md',
          description: 'System architecture and technical implementation',
          location: 'docs_prod/TECHNICAL_ARCHITECTURE.md'
        },
        {
          title: 'Knowledge Architecture',
          filePath: 'KNOWLEDGE_ARCHITECTURE.md',
          description: 'Knowledge system architecture and design',
          location: 'docs_prod/KNOWLEDGE_ARCHITECTURE.md'
        }
      ]
    },
    {
      id: 'canon',
      name: 'Canon System',
      icon: Database,
      color: 'amber',
      description: 'Canon Engine documentation and system guides',
      docs: [
        {
          title: 'Canon Implementation Summary',
          filePath: 'CANON_IMPLEMENTATION_SUMMARY.md',
          description: 'Canon Engine implementation details',
          location: 'docs_prod/CANON_IMPLEMENTATION_SUMMARY.md'
        },
        {
          title: 'Canon',
          filePath: 'CANON.md',
          description: 'Complete Canon Engine documentation',
          location: 'docs_prod/CANON.md'
        },
        {
          title: 'Canon System Guide',
          filePath: 'CANON_SYSTEM_GUIDE.md',
          description: 'Canon Engine system guide',
          location: 'docs_prod/CANON_SYSTEM_GUIDE.md'
        }
      ]
    },
    {
      id: 'frameworks',
      name: 'Frameworks & Specifications',
      icon: BookOpen,
      color: 'amber',
      description: 'Frameworks, specifications, and design documents',
      docs: [
        {
          title: 'Assessment Framework',
          filePath: 'ASSESSMENT_FRAMEWORK.md',
          description: 'Assessment framework documentation',
          location: 'docs_prod/ASSESSMENT_FRAMEWORK.md'
        }
      ]
    },
    {
      id: 'content',
      name: 'Content & CMS',
      icon: FileText,
      color: 'coral',
      description: 'Content management, creation guides, and CMS documentation',
      docs: [
        {
          title: 'Content Curation',
          filePath: 'sections/content-curation.md',
          description: 'Content curation and organization',
          location: 'docs_prod/sections/content-curation.md'
        },
        {
          title: 'Admin Guide',
          filePath: 'sections/admin-guide.md',
          description: 'CMS administration and management',
          location: 'docs_prod/sections/admin-guide.md'
        },
        {
          title: 'User Guide',
          filePath: 'sections/user-guide.md',
          description: 'End user guide for taking assessments',
          location: 'docs_prod/sections/user-guide.md'
        },
        {
          title: 'Project Context',
          filePath: 'sections/project-context.md',
          description: 'Comprehensive project context and system overview',
          location: 'docs_prod/sections/project-context.md'
        },
        {
          title: 'Technical Architecture',
          filePath: 'sections/technical-architecture.md',
          description: 'Detailed technical architecture documentation',
          location: 'docs_prod/sections/technical-architecture.md'
        }
      ]
    },
    {
      id: 'knowledge',
      name: 'Knowledge Engine',
      icon: Zap,
      color: 'primary',
      description: 'Knowledge Engine documentation and guides',
      docs: [
        {
          title: 'Knowledge Engine Documentation',
          filePath: 'sections/knowledge-engine-documentation-copy.md',
          description: 'Complete Knowledge Engine documentation',
          location: 'docs_prod/sections/knowledge-engine-documentation-copy.md'
        },
        {
          title: 'Knowledge Engine Quick Reference',
          filePath: 'sections/knowledge-engine-quick-reference-copy.md',
          description: 'Quick reference for Knowledge Engine',
          location: 'docs_prod/sections/knowledge-engine-quick-reference-copy.md'
        },
        {
          title: 'Knowledge Graph',
          filePath: 'sections/knowledge-graph-copy.md',
          description: 'Knowledge graph documentation',
          location: 'docs_prod/sections/knowledge-graph-copy.md'
        },
        {
          title: 'Knowledge Object Authoring',
          filePath: 'sections/knowledge-object-authoring-copy.md',
          description: 'Guide for authoring knowledge objects',
          location: 'docs_prod/sections/knowledge-object-authoring-copy.md'
        }
      ]
    },
    {
      id: 'meta-taxonomy',
      name: 'Meta-Taxonomy Framework',
      icon: Layers,
      color: 'sage',
      description: 'Meta-taxonomy framework and encoding documentation',
      docs: [
        {
          title: 'Meta-Taxonomy Quick Reference',
          filePath: 'sections/meta-taxonomy-quick-reference-copy.md',
          description: 'Quick reference for meta-taxonomy framework',
          location: 'docs_prod/sections/meta-taxonomy-quick-reference-copy.md'
        },
        {
          title: 'Meta-System Encoding Examples',
          filePath: 'sections/meta-system-encoding-examples-copy.md',
          description: 'Examples of meta-system encoding',
          location: 'docs_prod/sections/meta-system-encoding-examples-copy.md'
        },
        {
          title: 'Representational Grammar',
          filePath: 'sections/representational-grammar.md',
          description: 'Representational grammar documentation',
          location: 'docs_prod/sections/representational-grammar.md'
        }
      ]
    },
    {
      id: 'frameworks-specs',
      name: 'Frameworks & Specifications (Detailed)',
      icon: BookOpen,
      color: 'amber',
      description: 'Detailed framework and specification documents',
      docs: [
        {
          title: 'Peak Priorities Frameworks Master',
          filePath: 'sections/peak-priorities-frameworks-master.md',
          description: 'Master frameworks documentation',
          location: 'docs_prod/sections/peak-priorities-frameworks-master.md'
        },
        {
          title: 'Functional & Technical Build Specification',
          filePath: 'sections/peak-priorities-interpretive-assessment-functional-and-technical-build-specification.md',
          description: 'Functional and technical build specification',
          location: 'docs_prod/sections/peak-priorities-interpretive-assessment-functional-and-technical-build-specification.md'
        },
        {
          title: 'Visual Design Language v5',
          filePath: 'sections/peak-priorities-visual-design-language-v-5.md',
          description: 'Visual design language documentation',
          location: 'docs_prod/sections/peak-priorities-visual-design-language-v-5.md'
        }
      ]
    },
    {
      id: 'operations',
      name: 'Operations & Runbooks',
      icon: Settings,
      color: 'coral',
      description: 'Operations guides, runbooks, and ADRs',
      docs: [
        {
          title: 'Runbook',
          filePath: 'sections/runbook.md',
          description: 'Operations runbook',
          location: 'docs_prod/sections/runbook.md'
        },
        {
          title: 'ADR (Architecture Decision Records)',
          filePath: 'sections/adr.md',
          description: 'Architecture decision records',
          location: 'docs_prod/sections/adr.md'
        },
        {
          title: 'Canon System Development',
          filePath: 'sections/canon-system-development.md',
          description: 'Canon system development guide',
          location: 'docs_prod/sections/canon-system-development.md'
        }
      ]
    }
  ];

  const filteredCategories = categories.map(category => ({
    ...category,
    docs: category.docs.filter(doc =>
      doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.location.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => 
    category.docs.length > 0 || 
    category.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    category.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleViewDoc = (doc: DocItem) => {
    setViewingDoc(doc);
    setLoading(true);
    setError('');
    setMarkdown('');

    // Fetch from /docs_prod/ path (served by Vite plugin from root docs_prod folder)
    fetch(`/docs_prod/${doc.filePath}`)
      .then(r => {
        if (!r.ok) {
          throw new Error(`HTTP ${r.status}: ${r.statusText}`);
        }
        return r.text();
      })
      .then(content => {
        // Check if the response is HTML (Vite fallback) instead of markdown
        if (content.trim().startsWith('<!doctype html>') || content.trim().startsWith('<html')) {
          throw new Error('Document not found - server returned HTML fallback');
        }
        
        // Strip frontmatter metadata from display
        let cleanContent = content;
        if (content.startsWith('---')) {
          const frontmatterEnd = content.indexOf('---', 3);
          if (frontmatterEnd !== -1) {
            cleanContent = content.substring(frontmatterEnd + 3).trim();
          }
        }
        
        setMarkdown(cleanContent);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Failed to load document:', error);
        setError(`Document not found: ${doc.filePath}. ${error.message.includes('HTML fallback') ? 'The file may not exist in the wiki directory.' : error.message}`);
        setMarkdown('');
        setLoading(false);
      });
  };

  const getColorClasses = (color: string) => {
    const colors: Record<string, any> = {
      primary: {
        bg: 'bg-consulting-slate-blue-50',
        border: 'border-consulting-slate-blue-200',
        text: 'text-consulting-slate-blue-900',
        accent: 'text-consulting-slate-blue-600',
        badge: 'bg-consulting-slate-blue-100 text-consulting-slate-blue-800'
      },
      sage: {
        bg: 'bg-sage-50',
        border: 'border-sage-200',
        text: 'text-sage-900',
        accent: 'text-sage-600',
        badge: 'bg-sage-100 text-sage-800'
      },
      amber: {
        bg: 'bg-[var(--mustard-dim)]',
        border: 'border-[var(--mustard)]',
        text: 'text-[var(--mustard)]',
        accent: 'text-[var(--mustard)]',
        badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)]'
      },
      coral: {
        bg: 'bg-coral-50',
        border: 'border-coral-200',
        text: 'text-coral-900',
        accent: 'text-coral-600',
        badge: 'bg-coral-100 text-coral-800'
      }
    };
    return colors[color] || colors.primary;
  };

  // Document Viewer
  if (viewingDoc) {
    if (loading) {
      return (
        <div className="min-h-screen bg-charcoal-50 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-consulting-slate-blue-600"></div>
            <p className="mt-4 text-charcoal-600 font-body">Loading document...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="min-h-screen bg-charcoal-50 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-[var(--bg-raised)] border border-[var(--coral)] shadow-sm p-6">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-4 font-heading">Error loading document</h2>
            <p className="text-charcoal-700 mb-4 font-body">{error}</p>
            <p className="text-sm text-charcoal-600 mb-4 font-body">
              Tried to load: <code className="bg-[var(--coral-dim)] px-2 py-1 rounded font-mono text-xs">/docs_prod/{viewingDoc.filePath}</code>
            </p>
            <button
              onClick={() => {
                setViewingDoc(null);
                setMarkdown('');
                setError('');
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700 transition-colors font-medium"
            >
              Back to Documentation Hub
            </button>
          </div>
        </div>
      );
    }

    if (markdown) {
      return (
        <DocumentationViewer
          title={viewingDoc.title}
          description={viewingDoc.description}
          location={viewingDoc.location}
          markdown={markdown}
          onBack={() => {
            setViewingDoc(null);
            setMarkdown('');
            setError('');
          }}
        />
      );
    }

    return null;
  }

  // Main Hub View
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center space-x-3 mb-4">
          <div className="h-16 w-16 bg-consulting-slate-blue-600 flex items-center justify-center shadow-md">
            <BookOpen className="h-10 w-10 text-white" />
          </div>
          <div>
            <h1 className="text-4xl font-bold text-charcoal-900">
              Documentation Hub
            </h1>
            <p className="text-xl text-charcoal-600">
              Production documentation from docs_prod
            </p>
          </div>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <p className="text-lg text-charcoal-700 leading-relaxed">
            Browse and view all production documentation. Click on any document to view its full content.
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="max-w-2xl mx-auto">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-charcoal-400" />
          <input
            type="text"
            placeholder="Search documentation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border-2 border-charcoal-200 focus:border-consulting-slate-blue-500 focus:outline-none text-charcoal-900"
          />
        </div>
      </div>

      {/* Category Filters */}
      <div className="flex flex-wrap gap-2 justify-center">
        <button
          onClick={() => setSelectedCategory(null)}
          className={`px-4 py-2  border-2 transition-colors ${
            selectedCategory === null
              ? 'bg-consulting-slate-blue-100 border-consulting-slate-blue-300 text-consulting-slate-blue-900'
              : 'bg-[var(--bg-raised)] border-charcoal-200 text-charcoal-700 hover:bg-charcoal-50'
          }`}
        >
          All Categories
        </button>
        {categories.map((category) => {
          const colors = getColorClasses(category.color);
          return (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2  border-2 transition-colors ${
                selectedCategory === category.id
                  ? `${colors.bg} ${colors.border} ${colors.text}`
                  : 'bg-[var(--bg-raised)] border-charcoal-200 text-charcoal-700 hover:bg-charcoal-50'
              }`}
            >
              {category.name}
            </button>
          );
        })}
      </div>

      {/* Documentation Categories */}
      <div className="space-y-8">
        {filteredCategories
          .filter(category => selectedCategory === null || category.id === selectedCategory)
          .map((category) => {
            const Icon = category.icon;
            const colors = getColorClasses(category.color);
            
            return (
              <div key={category.id} className={`${colors.bg}  p-8 border-2 ${colors.border}`}>
                <div className="flex items-center gap-4 mb-6">
                  <div className={`p-3  ${colors.badge} border ${colors.border}`}>
                    <Icon className={`h-6 w-6 ${colors.accent}`} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-charcoal-900">{category.name}</h2>
                    <p className="text-charcoal-600">{category.description}</p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {category.docs.map((doc, index) => (
                    <button
                      key={index}
                      onClick={() => handleViewDoc(doc)}
                      className="bg-[var(--bg-raised)] p-4 border border-charcoal-200 hover:shadow-md transition-shadow text-left w-full"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-charcoal-900 text-sm">{doc.title}</h3>
                      </div>
                      <p className="text-xs text-charcoal-600 mb-3">{doc.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-charcoal-500 font-mono truncate">
                          {doc.location}
                        </span>
                        <ArrowRight className="h-4 w-4 text-consulting-slate-blue-600 flex-shrink-0" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
      </div>

      {/* Quick Stats */}
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Documentation Statistics</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-consulting-slate-blue-600">{categories.length}</div>
            <div className="text-sm text-charcoal-600">Categories</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-sage-600">
              {categories.reduce((sum, cat) => sum + cat.docs.length, 0)}
            </div>
            <div className="text-sm text-charcoal-600">Documents</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-[var(--mustard)]">
              {filteredCategories.reduce((sum, cat) => sum + cat.docs.length, 0)}
            </div>
            <div className="text-sm text-charcoal-600">Showing</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-charcoal-600">∞</div>
            <div className="text-sm text-charcoal-600">Possibilities</div>
          </div>
        </div>
      </div>
    </div>
  );
};
